import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Link,
  IconButton,
  Typography,
} from "@mui/material";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import ListResponsive from "../../components/other/ListResponsive";
import { updateCartItem } from "../../components/user/utils";
import { useUpdateCart } from "../../components/user/BCPAuthReducers";
import { BCPMinus, BCPPlus } from "../../components/icons/bcp/BCPIcons";
import { getData } from "../../components/utilities/UtilityFunctions";

const fetchFile = async (fileId) => {
  if (!fileId) return;

  var returnObj;
  try {
    returnObj = await getData(`/files/${fileId}`, {
      queryStringParameters: {},
    });
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

const ImagePreview = (props) => {
  const { fileId, alt, outerSX } = props;
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);
  useEffect(() => {
    const go = async () => {
      setLoading(true);
      if (!fileId) {
        setImage(null);
        setLoading(false);
        return;
      }

      if (image) {
        if (image.id === fileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = await fetchFile(fileId);
        if (file) {
          setImage({
            id: fileId,
            ...file,
          });
        }
      } catch (err) {
        //
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [fileId]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "50px",
        minHeight: "50px",
        maxHeight: "50px",
        p: 1,
        border: 1,
        borderColor: "divider",
        ...outerSX,
      }}
    >
      {!loading && image?.url && (
        <img
          src={image.url}
          alt={alt}
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        />
      )}

      {loading && <CircularProgress size={24} />}
    </Box>
  );
};

export default function StoreCart() {
  const authContext = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState([]);

  useEffect(() => {
    const go = async () => {
      setItems(authContext?.authState?.cart?.items || []);
      var newTotals = [];
      var newTotal = { qty: 0, value: 0 };
      if (authContext?.authState?.cart?.items?.length) {
        authContext?.authState?.cart.items.forEach((item) => {
          newTotal.qty += item.qty;
          newTotal.value += item.qty * item.lp;
        });
      }
      newTotals.push(newTotal);
      setTotals(newTotals);
      setLoading(false);
    };

    go();
  }, [authContext]);

  return (
    <>
      {!loading && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Grid container sx={{ gap: 2 }}>
            <Grid item xs={12} sx={{ p: 2 }}>
              <Typography variant="h3">Cart</Typography>
            </Grid>

            <Grid item xs={12} sx={{ p: 2 }}>
              <ListResponsive
                originalList={items}
                rowProps={{
                  headings: <RowItem heading />,
                  mapFunction: (row, index) => (
                    <RowItem key={index} row={row} />
                  ),
                }}
              />

              {totals?.length > 0 &&
                totals.map((total, index) => {
                  return <RowItem key={index} total={total} />;
                })}

              <Grid
                item
                xs={12}
                sx={{
                  mb: 1,
                  textAlign: "right",
                }}
              >
                <Button
                  href="/shop/checkout"
                  variant="contained"
                  color="success"
                >
                  Checkout
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      )}
      {loading && <ModalLoadingFull />}
    </>
  );
}

const RowItem = (props) => {
  const { heading, row, total } = props;
  const useUpdateCartReducer = useUpdateCart();

  return (
    <Grid
      item
      xs={12}
      sx={{
        p: 1,
        mb: 1,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: 1,
        boxShadow: 1,
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={6}>
          {heading ? (
            <Typography variant="body1">Product</Typography>
          ) : total ? (
            <></>
          ) : (
            <Grid container>
              <Grid item xs sx={{ maxWidth: ["50px", "70px"] }}>
                <ImagePreview fileId={row?.imageFileId} alt={row.name} />
              </Grid>
              <Grid item xs>
                <Link href={`/shop/product/${row.id}`} underline="none">
                  <Typography variant="body1">{row.name}</Typography>
                </Link>
                {row.categories?.length > 0 && (
                  <Typography variant="caption">
                    {row.categories.join(", ")}
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={2}>
          {heading ? (
            <Typography variant="body1">Price</Typography>
          ) : total ? (
            <></>
          ) : (
            <Typography variant="body1">{row.lp} LP</Typography>
          )}
        </Grid>
        <Grid
          item
          xs={2}
          sx={{
            textAlign: "center",
          }}
        >
          {heading ? (
            <Typography variant="body1">Quantity</Typography>
          ) : total ? (
            <Typography variant="body1">{total.qty}</Typography>
          ) : (
            <>
              <IconButton
                aria-label="Minus 1"
                edge="end"
                color="primary"
                size="small"
                onClick={() => {
                  updateCartItem(row, row.qty - 1, useUpdateCartReducer);
                }}
              >
                <BCPMinus fontSize="1x" />
              </IconButton>
              <Typography
                variant="caption"
                sx={{
                  textAlign: "center",
                  width: "30px",
                  minWidth: "30px",
                  display: "inline-block",
                }}
              >
                {row.qty}
              </Typography>
              <IconButton
                aria-label="Plus 1"
                edge="end"
                color="primary"
                size="small"
                onClick={() => {
                  updateCartItem(row, row.qty + 1, useUpdateCartReducer);
                }}
              >
                <BCPPlus fontSize="1x" />
              </IconButton>
            </>
          )}
        </Grid>
        <Grid
          item
          xs={2}
          sx={{
            textAlign: "right",
          }}
        >
          {heading ? (
            <Typography variant="body1">Subtotal</Typography>
          ) : total ? (
            <Typography variant="body1">{total.value} LP</Typography>
          ) : (
            <Typography variant="body1">{row.qty * row.lp} LP</Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
